import { SONGS, type Song, type SongName } from './music';
import { chordNotes, noteFreq } from './notes';

export type Sfx =
  | 'jump'
  | 'jumpBig'
  | 'coin'
  | 'bump'
  | 'break'
  | 'sprout'
  | 'grow'
  | 'shrink'
  | 'fire'
  | 'stomp'
  | 'kick'
  | 'die'
  | 'pipe'
  | 'oneup'
  | 'warn'
  | 'flag'
  | 'select'
  | 'pause';

/**
 * AudioManager — everything is synthesised at runtime with the Web Audio API. No sampled
 * or transcribed material from any other game is used.
 *
 * The context is created lazily: browsers require a user gesture before audio may start.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicBus!: GainNode;
  private sfxBus!: GainNode;
  private noiseBuf: AudioBuffer | null = null;

  musicOn = true;
  sfxOn = true;
  musicVol = 0.55;
  sfxVol = 0.8;

  private song: Song | null = null;
  private songName: SongName | null = null;
  private step = 0;
  private nextTime = 0;
  private timer: number | null = null;
  private paused = false;
  /** Song to return to after a one-shot jingle. */
  private resumeSong: SongName | null = null;

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Must be called from a user gesture handler. */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor =
        (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.ctx.destination);
      this.musicBus = this.ctx.createGain();
      this.musicBus.gain.value = this.musicOn ? this.musicVol : 0;
      this.musicBus.connect(this.master);
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.gain.value = this.sfxOn ? this.sfxVol : 0;
      this.sfxBus.connect(this.master);
      this.noiseBuf = this.makeNoise();
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setMusicVolume(v: number, on = this.musicOn): void {
    this.musicVol = clamp01(v);
    this.musicOn = on;
    if (this.ctx) this.musicBus.gain.value = this.musicOn && !this.paused ? this.musicVol : 0;
  }

  setSfxVolume(v: number, on = this.sfxOn): void {
    this.sfxVol = clamp01(v);
    this.sfxOn = on;
    if (this.ctx) this.sfxBus.gain.value = this.sfxOn ? this.sfxVol : 0;
  }

  /** Silence music while paused / unfocused without losing the playhead. */
  setPaused(p: boolean): void {
    this.paused = p;
    if (!this.ctx) return;
    this.musicBus.gain.value = this.musicOn && !p ? this.musicVol : 0;
    if (p) {
      this.stopScheduler();
    } else if (this.song) {
      this.nextTime = this.ctx.currentTime + 0.05;
      this.startScheduler();
    }
  }

  suspend(): void {
    void this.ctx?.suspend();
  }

  resume(): void {
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
  }

  /* ------------------------------- music ------------------------------- */

  playSong(name: SongName, restart = false): void {
    if (!this.ctx) return;
    if (this.songName === name && !restart) return;
    this.songName = name;
    this.song = SONGS[name];
    this.step = 0;
    this.nextTime = this.ctx.currentTime + 0.06;
    this.startScheduler();
  }

  /** Play a jingle, then go back to `back` (or silence). */
  playJingle(name: SongName, back: SongName | null = null): void {
    this.resumeSong = back;
    this.playSong(name, true);
  }

  stopMusic(): void {
    this.song = null;
    this.songName = null;
    this.stopScheduler();
  }

  private startScheduler(): void {
    if (this.timer != null || !this.ctx) return;
    this.timer = window.setInterval(() => this.tick(), 25);
  }

  private stopScheduler(): void {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    const ctx = this.ctx;
    const song = this.song;
    if (!ctx || !song) {
      this.stopScheduler();
      return;
    }
    const stepDur = 30 / song.bpm; // eighth note
    const horizon = ctx.currentTime + 0.15;
    while (this.nextTime < horizon) {
      this.scheduleStep(song, this.step, this.nextTime, stepDur);
      this.step++;
      this.nextTime += stepDur;
      const total = song.lead.length * 8;
      if (this.step >= total) {
        if (song.loop) {
          this.step = 0;
        } else {
          const back = this.resumeSong;
          this.resumeSong = null;
          this.song = null;
          this.songName = null;
          this.stopScheduler();
          if (back) {
            // small gap, then resume the level track
            window.setTimeout(() => this.playSong(back, true), 260);
          }
          return;
        }
      }
    }
  }

  private scheduleStep(song: Song, step: number, when: number, stepDur: number): void {
    const bar = Math.floor(step / 8) % song.lead.length;
    const slot = step % 8;

    const leadTok = tokenAt(song.lead[bar], slot);
    if (leadTok && leadTok !== '.' && leadTok !== '-') {
      const len = holdLength(song.lead[bar], slot) * stepDur;
      this.tone(noteFreq(leadTok), when, len * 0.92, song.wave, 0.16, 'music');
    }
    if (song.harmony) {
      const h = tokenAt(song.harmony[bar % song.harmony.length], slot);
      if (h && h !== '.' && h !== '-') {
        const len = holdLength(song.harmony[bar % song.harmony.length], slot) * stepDur;
        this.tone(noteFreq(h), when, len * 0.9, 'square', 0.075, 'music');
      }
    }
    // bass: root - root - fifth - root walking pattern
    const chord = song.chords[Math.floor(step / 8) % song.chords.length];
    const notes = chordNotes(chord, song.bassOct);
    if (notes.length && slot % 2 === 0) {
      const pick = slot === 4 ? notes[2] : notes[0];
      this.tone(noteFreq(pick), when, stepDur * 1.7, 'triangle', 0.19, 'music');
    }
    // drums
    const d = song.drums[slot % song.drums.length];
    if (d === 'K') this.thump(when, 0.16);
    else if (d === 's') this.noise(when, 0.09, 1400, 0.09);
    else if (d === 'h') this.noise(when, 0.035, 5200, 0.045);
  }

  /* -------------------------------- sfx -------------------------------- */

  play(name: Sfx): void {
    if (!this.ctx || !this.sfxOn) return;
    const t = this.ctx.currentTime;
    switch (name) {
      case 'jump':
        this.sweep(300, 640, t, 0.16, 'square', 0.2);
        break;
      case 'jumpBig':
        this.sweep(240, 540, t, 0.2, 'square', 0.22);
        break;
      case 'coin':
        this.tone(noteFreq('B5'), t, 0.06, 'square', 0.18, 'sfx');
        this.tone(noteFreq('E6'), t + 0.06, 0.16, 'square', 0.18, 'sfx');
        break;
      case 'bump':
        this.sweep(220, 120, t, 0.09, 'square', 0.18);
        this.noise(t, 0.05, 500, 0.07);
        break;
      case 'break':
        this.noise(t, 0.22, 2200, 0.16);
        this.sweep(400, 90, t, 0.22, 'sawtooth', 0.1);
        break;
      case 'sprout':
        this.sweep(180, 900, t, 0.42, 'triangle', 0.16);
        break;
      case 'grow':
        [0, 0.07, 0.14, 0.21, 0.28].forEach((d, i) => {
          this.tone(noteFreq(['C5', 'E5', 'G5', 'C6', 'E6'][i]), t + d, 0.09, 'square', 0.15, 'sfx');
        });
        break;
      case 'shrink':
        [0, 0.07, 0.14, 0.21].forEach((d, i) => {
          this.tone(noteFreq(['G5', 'E5', 'C5', 'G4'][i]), t + d, 0.1, 'square', 0.14, 'sfx');
        });
        break;
      case 'fire':
        this.sweep(900, 260, t, 0.13, 'sawtooth', 0.13);
        this.noise(t, 0.07, 3200, 0.06);
        break;
      case 'stomp':
        this.sweep(520, 150, t, 0.12, 'square', 0.2);
        this.noise(t, 0.06, 900, 0.09);
        break;
      case 'kick':
        this.sweep(700, 220, t, 0.1, 'square', 0.18);
        this.noise(t, 0.05, 2400, 0.08);
        break;
      case 'die':
        this.tone(noteFreq('B4'), t, 0.12, 'square', 0.18, 'sfx');
        this.tone(noteFreq('F4'), t + 0.14, 0.12, 'square', 0.18, 'sfx');
        this.sweep(420, 60, t + 0.3, 0.75, 'square', 0.18);
        break;
      case 'pipe':
        this.sweep(560, 90, t, 0.42, 'triangle', 0.2);
        this.noise(t, 0.3, 700, 0.07);
        break;
      case 'oneup':
        [0, 0.09, 0.18, 0.27].forEach((d, i) => {
          this.tone(noteFreq(['E5', 'G5', 'B5', 'E6'][i]), t + d, 0.11, 'triangle', 0.17, 'sfx');
        });
        break;
      case 'warn':
        this.tone(noteFreq('E6'), t, 0.07, 'square', 0.14, 'sfx');
        this.tone(noteFreq('E6'), t + 0.12, 0.07, 'square', 0.14, 'sfx');
        break;
      case 'flag':
        this.sweep(260, 1300, t, 0.5, 'triangle', 0.17);
        break;
      case 'select':
        this.tone(noteFreq('A5'), t, 0.05, 'square', 0.14, 'sfx');
        break;
      case 'pause':
        this.tone(noteFreq('D5'), t, 0.07, 'square', 0.12, 'sfx');
        this.tone(noteFreq('A4'), t + 0.08, 0.09, 'square', 0.12, 'sfx');
        break;
    }
  }

  /* ------------------------------ synthesis ------------------------------ */

  private bus(kind: 'music' | 'sfx'): GainNode {
    return kind === 'music' ? this.musicBus : this.sfxBus;
  }

  private tone(
    freq: number,
    when: number,
    dur: number,
    wave: OscillatorType,
    level: number,
    kind: 'music' | 'sfx',
  ): void {
    const ctx = this.ctx;
    if (!ctx || freq <= 0) return;
    const osc = ctx.createOscillator();
    osc.type = wave;
    osc.frequency.setValueAtTime(freq, when);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(level, when + 0.008);
    g.gain.setValueAtTime(level, when + Math.max(0.02, dur * 0.6));
    g.gain.exponentialRampToValueAtTime(0.0008, when + dur);
    osc.connect(g).connect(this.bus(kind));
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  private sweep(
    from: number,
    to: number,
    when: number,
    dur: number,
    wave: OscillatorType,
    level: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = wave;
    osc.frequency.setValueAtTime(from, when);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), when + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, when);
    g.gain.exponentialRampToValueAtTime(0.0008, when + dur);
    osc.connect(g).connect(this.sfxBus);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  private makeNoise(): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * 0.5);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let s = 0x1234abcd;
    for (let i = 0; i < len; i++) {
      s ^= s << 13;
      s ^= s >>> 17;
      s ^= s << 5;
      data[i] = ((s >>> 0) / 4294967296) * 2 - 1;
    }
    return buf;
  }

  private noise(when: number, dur: number, cutoff: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.noiseBuf) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.value = cutoff;
    filt.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, when);
    g.gain.exponentialRampToValueAtTime(0.0005, when + dur);
    src.connect(filt).connect(g).connect(this.sfxBus);
    src.start(when);
    src.stop(when + dur + 0.02);
  }

  /** Kick-style drum used by the music bus. */
  private thump(when: number, level: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(150, when);
    osc.frequency.exponentialRampToValueAtTime(48, when + 0.1);
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, when);
    g.gain.exponentialRampToValueAtTime(0.0008, when + 0.13);
    osc.connect(g).connect(this.musicBus);
    osc.start(when);
    osc.stop(when + 0.15);
  }
}

function tokenAt(bar: string, slot: number): string | undefined {
  return bar.trim().split(/\s+/)[slot];
}

/** How many steps a note is held for (counting following '-' tokens). */
function holdLength(bar: string, slot: number): number {
  const toks = bar.trim().split(/\s+/);
  let n = 1;
  for (let i = slot + 1; i < toks.length && toks[i] === '-'; i++) n++;
  return n;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
